<template>
  <div class="p-6">
    <!-- Encabezdo -->
    <div class="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
      <div>
        <h1 class="text-2xl font-bold text-gray-800 dark:text-white">Productos en Recepción</h1>
        <p class="text-gray-600 dark:text-gray-400">Administra los productos recibidos</p>
      </div>
      <div class="flex items-center gap-3">
        <!-- Botón Exportar / Imprimir -->
        <div class="relative group">
          <button
            @click="showExportMenu = !showExportMenu"
            class="flex items-center px-4 py-2.5 bg-gradient-to-r from-gray-800 to-gray-900 text-white rounded-lg hover:from-gray-900 hover:to-black transition-all duration-300 shadow-md hover:shadow-lg"
          >
            <Icon icon="mdi:printer" class="mr-2" />
            Imprimir
            <Icon icon="mdi:chevron-down" class="ml-2" />
          </button>
          
          <!-- Menú Exportar / Imprimir -->
          <div v-if="showExportMenu" 
               class="absolute right-0 mt-2 w-56 bg-gray-900 rounded-xl shadow-lg border border-gray-700 z-50">
            <div class="py-2">
              <button
                @click="printFullReport"
                class="w-full px-4 py-3 text-left hover:bg-gray-800 flex items-center text-gray-200"
              >
                <Icon icon="mdi:file-pdf-box" class="mr-3 text-red-500" />
                <div>
                  <p class="font-medium">Reporte Completo</p>
                  <p class="text-xs text-gray-400">Con estadísticas y tabla</p>
                </div>
              </button>
              <button
                @click="printSimplifiedReport"
                class="w-full px-4 py-3 text-left hover:bg-gray-800 flex items-center text-gray-200"
              >
                <Icon icon="mdi:table" class="mr-3 text-blue-500" />
                <div>
                  <p class="font-medium">Listado Simplificado</p>
                  <p class="text-xs text-gray-400">Solo tabla de productos</p>
                </div>
              </button>
              <div class="border-t border-gray-700 my-2"></div>
              <button
                @click="printCurrentView"
                class="w-full px-4 py-3 text-left hover:bg-gray-800 flex items-center text-gray-200"
              >
                <Icon icon="mdi:eye" class="mr-3 text-green-500" />
                <div>
                  <p class="font-medium">Vista Actual</p>
                  <p class="text-xs text-gray-400">Con filtros aplicados</p>
                </div>
              </button>
            </div>
          </div>
        </div>
        
        <button
          @click="openCreateModal"
          class="flex items-center px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-md hover:shadow-lg"
        >
          <Icon icon="mdi:plus" class="mr-2" />
          Nuevo Producto
        </button>
      </div>
    </div>

    <!-- Filtros y búsqueda -->
    <div class="bg-white dark:bg-gray-800 rounded-xl shadow-md p-5 mb-6 border border-gray-200 dark:border-gray-700">
      <div class="grid grid-cols-1 md:grid-cols-12 gap-4">
        <div class="md:col-span-4 lg:col-span-3">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Buscar productos
          </label>
          <div class="relative">
            <Icon icon="mdi:magnify" class="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-gray-400 text-lg" />
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Código, nombre, marca..."
              class="w-full pl-11 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
              @input="handleSearch"
            />
          </div>
        </div>
        <div class="md:col-span-2 lg:col-span-2">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Estado
          </label>
          <div class="relative">
            <select
              v-model="filterStatus"
              class="w-full appearance-none border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10 transition-all duration-300"
              @change="handleFilterChange"
            >
              <option value="all">Todos</option>
              <option value="disponible">Disponible</option>
              <option value="vendido">Vendido</option>
              <option value="reservado">Reservado</option>
            </select>
            <Icon icon="mdi:chevron-down" class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>
        </div>
        <div class="md:col-span-3 lg:col-span-2">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Categoría
          </label>
          <div class="relative">
            <select
              v-model="filterCategory"
              class="w-full appearance-none border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10 transition-all duration-300"
              @change="handleFilterChange"
            >
              <option value="0">Todas</option>
              <option v-for="cat in categorias" :key="cat.idCategoria" :value="cat.idCategoria">
                {{ cat.nombre }}
              </option>
            </select>
            <Icon icon="mdi:chevron-down" class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>
        </div>
        <div class="md:col-span-3 lg:col-span-2">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Ordenar por
          </label>
          <div class="relative">
            <select
              v-model="sortBy"
              class="w-full appearance-none border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10 transition-all duration-300"
            >
              <option value="nombre">Nombre (A-Z)</option>
              <option value="nombre_desc">Nombre (Z-A)</option>
              <option value="codigo">Código</option>
              <option value="precio_desc">Precio (mayor a menor)</option>
              <option value="precio">Precio (menor a mayor)</option>
              <option value="fechaCreacion_desc">Más recientes</option>
              <option value="fechaCreacion">Más antiguas</option>
            </select>
            <Icon icon="mdi:sort" class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>
        </div>
        <div class="md:col-span-4 lg:col-span-3">
          <div class="grid grid-cols-2 gap-2">
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Precio Min ($)
              </label>
              <input
                v-model.number="precioMin"
                type="number"
                placeholder="Min"
                class="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                @input="handleFilterChange"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Precio Max ($)
              </label>
              <input
                v-model.number="precioMax"
                type="number"
                placeholder="Max"
                class="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                @input="handleFilterChange"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Estadísticas -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
      <div class="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 p-5 rounded-xl border border-blue-200 dark:border-blue-700 shadow-sm">
        <div class="flex items-center">
          <div class="p-3 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg shadow-md">
            <Icon icon="mdi:package-variant" class="text-white text-2xl" />
          </div>
          <div class="ml-4">
            <p class="text-sm text-gray-600 dark:text-gray-400 font-medium">Total Productos</p>
            <div class="flex items-baseline">
              <p class="text-3xl font-bold text-gray-800 dark:text-white">{{ productos.length }}</p>
              <span class="ml-2 text-xs px-2 py-1 bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 rounded-full">
                {{ filteredProductos.length }} filtrados
              </span>
            </div>
          </div>
        </div>
      </div>
      <div class="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 p-5 rounded-xl border border-green-200 dark:border-green-700 shadow-sm">
        <div class="flex items-center">
          <div class="p-3 bg-gradient-to-r from-green-500 to-green-600 rounded-lg shadow-md">
            <Icon icon="mdi:check-circle" class="text-white text-2xl" />
          </div>
          <div class="ml-4">
            <p class="text-sm text-gray-600 dark:text-gray-400 font-medium">Disponibles</p>
            <div class="flex items-baseline">
              <p class="text-3xl font-bold text-gray-800 dark:text-white">{{ estadisticas.disponibles }}</p>
              <span class="ml-2 text-xs px-2 py-1 bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200 rounded-full">
                {{ estadisticas.porcentajeDisponibles }}%
              </span>
            </div>
          </div>
        </div>
      </div>
      <div class="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 p-5 rounded-xl border border-blue-200 dark:border-blue-700 shadow-sm">
        <div class="flex items-center">
          <div class="p-3 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg shadow-md">
            <Icon icon="mdi:shopping" class="text-white text-2xl" />
          </div>
          <div class="ml-4">
            <p class="text-sm text-gray-600 dark:text-gray-400 font-medium">Vendidos</p>
            <p class="text-3xl font-bold text-gray-800 dark:text-white">{{ estadisticas.vendidos }}</p>
          </div>
        </div>
      </div>
      <div class="bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-800/20 p-5 rounded-xl border border-amber-200 dark:border-amber-700 shadow-sm">
        <div class="flex items-center">
          <div class="p-3 bg-gradient-to-r from-amber-500 to-amber-600 rounded-lg shadow-md">
            <Icon icon="mdi:clock-outline" class="text-white text-2xl" />
          </div>
          <div class="ml-4">
            <p class="text-sm text-gray-600 dark:text-gray-400 font-medium">Reservados</p>
            <p class="text-3xl font-bold text-gray-800 dark:text-white">{{ estadisticas.reservados }}</p>
          </div>
        </div>
      </div>
      <div class="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 p-5 rounded-xl border border-purple-200 dark:border-purple-700 shadow-sm">
        <div class="flex items-center">
          <div class="p-3 bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg shadow-md">
            <Icon icon="mdi:currency-usd" class="text-white text-2xl" />
          </div>
          <div class="ml-4">
            <p class="text-sm text-gray-600 dark:text-gray-400 font-medium">Valor Total</p>
            <p class="text-2xl font-bold text-gray-800 dark:text-white">${{ formatearMonto(estadisticas.valorTotal) }}</p>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {{ estadisticas.recientes }} recientes
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Indicadores de filtro -->
    <div v-if="isFiltered" class="mb-4">
      <div class="flex flex-wrap items-center gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3">
        <span class="text-sm font-medium text-amber-800 dark:text-amber-300">
          <Icon icon="mdi:filter" class="inline mr-1" />
          Filtros aplicados:
        </span>
        <span v-if="searchQuery" class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200">
          Buscar: "{{ searchQuery }}"
          <button @click="searchQuery = ''; handleSearch()" class="ml-1.5 hover:text-blue-900">
            <Icon icon="mdi:close" class="text-xs" />
          </button>
        </span>
        <span v-if="filterStatus !== 'all'" class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-800 dark:text-indigo-200">
          Estado: {{ filterStatus === 'disponible' ? 'Disponible' : filterStatus === 'vendido' ? 'Vendido' : 'Reservado' }}
          <button @click="filterStatus = 'all'; handleFilterChange()" class="ml-1.5 hover:text-indigo-900">
            <Icon icon="mdi:close" class="text-xs" />
          </button>
        </span>
        <span v-if="filterCategory !== 0" class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-800 dark:text-purple-200">
          Categoría: {{ getCategoriaNombre(filterCategory) }}
          <button @click="filterCategory = 0; handleFilterChange()" class="ml-1.5 hover:text-purple-900">
            <Icon icon="mdi:close" class="text-xs" />
          </button>
        </span>
        <span v-if="precioMin || precioMax" class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-200">
          Precio: ${{ precioMin || 0 }} - ${{ precioMax || '∞' }}
          <button @click="precioMin = null; precioMax = null; handleFilterChange()" class="ml-1.5 hover:text-green-900">
            <Icon icon="mdi:close" class="text-xs" />
          </button>
        </span>
        <button
          @click="clearFilters"
          class="text-sm text-amber-700 dark:text-amber-400 hover:text-amber-900 dark:hover:text-amber-300 font-medium"
        >
          <Icon icon="mdi:filter-off" class="inline mr-1" />
          Limpiar filtros
        </button>
      </div>
    </div>

    <!-- Tabla de productos -->
    <div class="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden border border-gray-200 dark:border-gray-700">
      <!-- Encabezado de tabla -->
      <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
        <div>
          <h2 class="text-lg font-semibold text-gray-800 dark:text-white">Lista de Productos</h2>
          <p class="text-sm text-gray-600 dark:text-gray-400">
            Mostrando {{ filteredProductos.length }} de {{ productos.length }} productos
          </p>
        </div>
        <div class="flex items-center space-x-2">
          <div class="text-sm text-gray-500 dark:text-gray-400">
            <Icon icon="mdi:calendar" class="inline mr-1" />
            Última actualización: {{ formatTime(lastUpdate) }}
          </div>
        </div>
      </div>

      <!-- Tabla -->
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead class="bg-gray-50 dark:bg-gray-700/50">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                <div class="flex items-center space-x-1">
                  <span>Código</span>
                  <button @click="sortBy = sortBy === 'codigo' ? 'codigo_desc' : 'codigo'">
                    <Icon icon="mdi:sort" class="text-gray-400 hover:text-gray-600" />
                  </button>
                </div>
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                <div class="flex items-center space-x-1">
                  <span>Producto</span>
                  <button @click="sortBy = sortBy === 'nombre' ? 'nombre_desc' : 'nombre'">
                    <Icon icon="mdi:sort-alphabetical" class="text-gray-400 hover:text-gray-600" />
                  </button>
                </div>
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Marca/Modelo
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Categoría
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                <div class="flex items-center space-x-1">
                  <span>Precio</span>
                  <button @click="sortBy = sortBy === 'precio' ? 'precio_desc' : 'precio'">
                    <Icon icon="mdi:sort-numeric" class="text-gray-400 hover:text-gray-600" />
                  </button>
                </div>
              </th>
               <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Acciones
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Estado
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Fecha
              </th>
             
            </tr>
          </thead>
          <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            <!-- Estado de carga -->
            <tr v-if="loading">
              <td colspan="8" class="px-6 py-12">
                <div class="flex flex-col items-center justify-center">
                  <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mb-4"></div>
                  <p class="text-gray-600 dark:text-gray-400">Cargando productos...</p>
                </div>
              </td>
            </tr>

            <!-- Sin resultados -->
            <tr v-else-if="filteredProductos.length === 0">
              <td colspan="8" class="px-6 py-16 text-center">
                <div class="flex flex-col items-center">
                  <div class="p-4 bg-gray-100 dark:bg-gray-700 rounded-full mb-4">
                    <Icon icon="mdi:package-variant-closed" class="text-3xl text-gray-400" />
                  </div>
                  <h3 class="text-lg font-medium text-gray-800 dark:text-white mb-2">
                    No se encontraron productos
                  </h3>
                  <p class="text-gray-600 dark:text-gray-400 mb-6 max-w-md">
                    {{ searchQuery || filterStatus !== 'all' || filterCategory !== 0 || precioMin || precioMax
                      ? 'Intenta con otros términos de búsqueda o cambia los filtros.' 
                      : 'No hay productos registrados. ¡Crea el primero!' }}
                  </p>
                  <button
                    v-if="!searchQuery && filterStatus === 'all' && filterCategory === 0 && !precioMin && !precioMax"
                    @click="openCreateModal"
                    class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Icon icon="mdi:plus" class="mr-2 inline" />
                    Crear primer producto
                  </button>
                  <button
                    v-else
                    @click="clearFilters"
                    class="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                  >
                    <Icon icon="mdi:filter-off" class="mr-2 inline" />
                    Limpiar filtros
                  </button>
                </div>
              </td>
            </tr>

            <!-- Lista de productos -->
            <tr 
              v-else 
              v-for="producto in paginatedProductos" 
              :key="producto.idProductoRecepcion"
              class="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-150 cursor-pointer"
              @click="openDetailModal(producto)"
            >
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="text-sm font-mono font-medium text-gray-900 dark:text-white">
                  {{ producto.codigo }}
                </div>
              </td>
              <td class="px-6 py-4">
                <div class="flex items-center">
                  <div class="flex-shrink-0 h-12 w-12">
                    <div :class="[
                      'h-12 w-12 rounded-xl flex items-center justify-center shadow-sm',
                      producto.estado === 'disponible'
                        ? 'bg-gradient-to-br from-green-100 to-emerald-100 dark:from-green-900 dark:to-emerald-900'
                        : producto.estado === 'vendido'
                        ? 'bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900 dark:to-indigo-900'
                        : 'bg-gradient-to-br from-amber-100 to-orange-100 dark:from-amber-900 dark:to-orange-900'
                    ]">
                      <Icon 
                        icon="mdi:package-variant" 
                        :class="[
                          'text-xl',
                          producto.estado === 'disponible' ? 'text-green-600 dark:text-green-400' :
                          producto.estado === 'vendido' ? 'text-blue-600 dark:text-blue-400' :
                          'text-amber-600 dark:text-amber-400'
                        ]" 
                      />
                    </div>
                  </div>
                  <div class="ml-4">
                    <div class="text-sm font-semibold text-gray-900 dark:text-white">
                      {{ producto.nombre }}
                    </div>
                    <div class="text-xs text-gray-500 dark:text-gray-400 flex items-center mt-1">
                      <Icon icon="mdi:identifier" class="mr-1" />
                      ID: {{ producto.idProductoRecepcion }}
                    </div>
                  </div>
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="text-sm text-gray-900 dark:text-white">
                  {{ producto.marca || '-' }}
                </div>
                <div class="text-xs text-gray-500 dark:text-gray-400">
                  {{ producto.modelo || '-' }}
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span class="px-2 py-1 text-xs rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                  {{ producto.nombreCategoria || '-' }}
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="text-lg font-semibold text-green-600 dark:text-green-400">
                  ${{ formatearMonto(producto.precio_estimado_usd) }}
                </div>
              </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <div class="flex items-center space-x-2">
                  <button
                    @click.stop="openEditModal(producto)"
                    class="p-2 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-800/50 transition-colors"
                    title="Editar"
                  >
                    <Icon icon="mdi:pencil" />
                  </button>
                  <button
                    v-if="producto.estado === 'disponible'"
                    @click.stop="marcarComoVendido(producto)"
                    class="p-2 bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-lg hover:bg-green-100 dark:hover:bg-green-800/50 transition-colors"
                    title="Marcar como vendido"
                  >
                    <Icon icon="mdi:shopping" />
                  </button>
                  <button
                    @click.stop="confirmDelete(producto)"
                    class="p-2 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-800/50 transition-colors"
                    title="Eliminar"
                  >
                    <Icon icon="mdi:delete" />
                  </button>
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span
                  @click.stop="toggleStatus(producto)"
                  :class="[
                    'px-3 py-1.5 inline-flex text-xs leading-4 font-semibold rounded-full shadow-sm transition-all duration-300 cursor-pointer',
                    producto.estado === 'disponible' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                    producto.estado === 'vendido' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                    'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200'
                  ]"
                >
                  <Icon :icon="producto.estado === 'disponible' ? 'mdi:check-circle' : producto.estado === 'vendido' ? 'mdi:shopping' : 'mdi:clock-outline'" class="mr-1.5" />
                  {{ convertirEstado(producto.estado) }}
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="text-sm text-gray-900 dark:text-white">
                  {{ formatDate(producto.fecha_creacion) }}
                </div>
                <div class="text-xs text-gray-500 dark:text-gray-400">
                  {{ formatTime(producto.fecha_creacion) }}
                </div>
              </td>
            
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Paginación -->
      <div v-if="filteredProductos.length > 0 && !loading" class="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <div class="text-sm text-gray-700 dark:text-gray-300">
          Mostrando <span class="font-semibold">{{ startItem }}-{{ endItem }}</span> de <span class="font-semibold">{{ filteredProductos.length }}</span> resultados
        </div>
        <div class="flex items-center space-x-2">
          <button
            @click="currentPage = Math.max(currentPage - 1, 1)"
            :disabled="currentPage === 1"
            :class="[
              'px-3 py-1 rounded-lg border transition-colors',
              currentPage === 1
                ? 'border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
            ]"
          >
            <Icon icon="mdi:chevron-left" />
          </button>
          
          <div class="flex space-x-1">
            <button
              v-for="page in visiblePages"
              :key="page"
              @click="currentPage = page"
              :class="[
                'w-8 h-8 rounded-lg flex items-center justify-center text-sm transition-colors',
                currentPage === page
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              ]"
            >
              {{ page }}
            </button>
          </div>
          
          <button
            @click="currentPage = Math.min(currentPage + 1, totalPages)"
            :disabled="currentPage === totalPages"
            :class="[
              'px-3 py-1 rounded-lg border transition-colors',
              currentPage === totalPages
                ? 'border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
            ]"
          >
            <Icon icon="mdi:chevron-right" />
          </button>
        </div>
      </div>
    </div>

    <!-- Modal para crear/editar producto -->
    <div v-if="showModal" class="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50 transition-opacity duration-300">
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg transform transition-all duration-300 scale-100">
        <!-- Encabezado del modal -->
        <div class="px-8 pt-8 pb-6 border-b border-gray-200 dark:border-gray-700">
          <div class="flex items-center justify-between">
            <div class="flex items-center">
              <div class="p-3 rounded-xl bg-gradient-to-r from-purple-500 to-indigo-500 shadow-md mr-4">
                <Icon :icon="editingProducto ? 'mdi:pencil-box' : 'mdi:package-variant-plus'" class="text-white text-2xl" />
              </div>
              <div>
                <h3 class="text-xl font-bold text-gray-900 dark:text-white">
                  {{ editingProducto ? 'Editar Producto' : 'Nuevo Producto' }}
                </h3>
                <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {{ editingProducto ? 'Modifica los datos del producto' : 'Completa los datos para crear un nuevo producto' }}
                </p>
              </div>
            </div>
            <button 
              @click="closeModal"
              class="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
            >
              <Icon icon="mdi:close" class="text-xl" />
            </button>
          </div>
        </div>
        
        <!-- Formulario -->
        <form @submit.prevent="saveProducto" class="px-8 py-6 max-h-[80vh] overflow-y-auto">
          <div class="space-y-6">
            <!-- Código y Nombre -->
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Código *
                </label>
                <div class="relative">
                  <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Icon icon="mdi:barcode" class="text-gray-400" />
                  </div>
                  <input
                    v-model="form.codigo"
                    type="text"
                    required
                    maxlength="50"
                    class="w-full pl-10 pr-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all duration-300"
                    placeholder="Ej: PROD-001"
                    :class="{ 'border-red-500': errors.codigo }"
                  />
                </div>
                <p v-if="errors.codigo" class="mt-2 text-sm text-red-600 dark:text-red-400">
                  {{ errors.codigo }}
                </p>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Nombre *
                </label>
                <div class="relative">
                  <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Icon icon="mdi:tag" class="text-gray-400" />
                  </div>
                  <input
                    v-model="form.nombre"
                    type="text"
                    required
                    maxlength="200"
                    class="w-full pl-10 pr-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all duration-300"
                    placeholder="Nombre del producto"
                    :class="{ 'border-red-500': errors.nombre }"
                  />
                </div>
                <p v-if="errors.nombre" class="mt-2 text-sm text-red-600 dark:text-red-400">
                  {{ errors.nombre }}
                </p>
              </div>
            </div>
            
            <!-- Descripción -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Descripción
              </label>
              <div class="relative">
                <div class="absolute top-3 left-3 pointer-events-none">
                  <Icon icon="mdi:text-box" class="text-gray-400" />
                </div>
                <textarea
                  v-model="form.descripcion"
                  rows="3"
                  maxlength="500"
                  class="w-full pl-10 pr-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all duration-300 resize-none"
                  placeholder="Describe el producto..."
                ></textarea>
                <div class="absolute bottom-3 right-3">
                  <span class="text-xs text-gray-500">{{ form.descripcion.length }}/500</span>
                </div>
              </div>
            </div>

            <!-- Marca y Modelo -->
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Marca
                </label>
                <input
                  v-model="form.marca"
                  type="text"
                  maxlength="100"
                  class="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all duration-300"
                  placeholder="Marca del producto"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Modelo
                </label>
                <input
                  v-model="form.modelo"
                  type="text"
                  maxlength="100"
                  class="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all duration-300"
                  placeholder="Modelo del producto"
                />
              </div>
            </div>
            
            <!-- Precio y Categoría -->
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Precio Estimado (USD) *
                </label>
                <div class="relative">
                  <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Icon icon="mdi:currency-usd" class="text-gray-400" />
                  </div>
                  <input
                    v-model.number="form.precio_estimado_usd"
                    type="number"
                    step="0.01"
                    min="0"
                    required
                    class="w-full pl-10 pr-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all duration-300"
                    placeholder="0.00"
                    :class="{ 'border-red-500': errors.precio }"
                  />
                </div>
                <p v-if="errors.precio" class="mt-2 text-sm text-red-600 dark:text-red-400">
                  {{ errors.precio }}
                </p>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Categoría *
                </label>
                <div class="relative">
                  <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Icon icon="mdi:folder" class="text-gray-400" />
                  </div>
                  <select
                    v-model="form.idCategoria"
                    required
                    class="w-full pl-10 pr-10 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all duration-300 appearance-none"
                    :class="{ 'border-red-500': errors.idCategoria }"
                  >
                    <option value="">Seleccione una categoría</option>
                    <option v-for="cat in categorias" :key="cat.idCategoria" :value="cat.idCategoria">
                      {{ cat.nombre }}
                    </option>
                  </select>
                  <Icon icon="mdi:chevron-down" class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
                </div>
                <p v-if="errors.idCategoria" class="mt-2 text-sm text-red-600 dark:text-red-400">
                  {{ errors.idCategoria }}
                </p>
              </div>
            </div>
            
            <!-- Estado -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Estado
              </label>
              <div class="grid grid-cols-3 gap-3">
                <label class="flex items-center p-3 border-2 rounded-xl cursor-pointer transition-all duration-300"
                  :class="form.estado === 'disponible' 
                    ? 'border-green-500 bg-green-50 dark:bg-green-900/30' 
                    : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 hover:border-gray-400'">
                  <input type="radio" v-model="form.estado" value="disponible" class="sr-only">
                  <div class="flex items-center w-full">
                    <div class="h-6 w-6 rounded-full border-2 flex items-center justify-center mr-3"
                      :class="form.estado === 'disponible' 
                        ? 'border-green-500 bg-green-500' 
                        : 'border-gray-400'">
                      <Icon v-if="form.estado === 'disponible'" icon="mdi:check" class="text-white text-xs" />
                    </div>
                    <div>
                      <p class="font-medium text-gray-900 dark:text-white">Disponible</p>
                      <p class="text-xs text-gray-500 dark:text-gray-400">Producto listo para venta</p>
                    </div>
                    <Icon icon="mdi:check-circle" class="ml-auto text-green-500" v-if="form.estado === 'disponible'" />
                  </div>
                </label>
                
                <label class="flex items-center p-3 border-2 rounded-xl cursor-pointer transition-all duration-300"
                  :class="form.estado === 'reservado' 
                    ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/30' 
                    : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 hover:border-gray-400'">
                  <input type="radio" v-model="form.estado" value="reservado" class="sr-only">
                  <div class="flex items-center w-full">
                    <div class="h-6 w-6 rounded-full border-2 flex items-center justify-center mr-3"
                      :class="form.estado === 'reservado' 
                        ? 'border-amber-500 bg-amber-500' 
                        : 'border-gray-400'">
                      <Icon v-if="form.estado === 'reservado'" icon="mdi:check" class="text-white text-xs" />
                    </div>
                    <div>
                      <p class="font-medium text-gray-900 dark:text-white">Reservado</p>
                      <p class="text-xs text-gray-500 dark:text-gray-400">Producto reservado</p>
                    </div>
                    <Icon icon="mdi:clock" class="ml-auto text-amber-500" v-if="form.estado === 'reservado'" />
                  </div>
                </label>

                <label class="flex items-center p-3 border-2 rounded-xl cursor-pointer transition-all duration-300"
                  :class="form.estado === 'vendido' 
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30' 
                    : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 hover:border-gray-400'">
                  <input type="radio" v-model="form.estado" value="vendido" class="sr-only">
                  <div class="flex items-center w-full">
                    <div class="h-6 w-6 rounded-full border-2 flex items-center justify-center mr-3"
                      :class="form.estado === 'vendido' 
                        ? 'border-blue-500 bg-blue-500' 
                        : 'border-gray-400'">
                      <Icon v-if="form.estado === 'vendido'" icon="mdi:check" class="text-white text-xs" />
                    </div>
                    <div>
                      <p class="font-medium text-gray-900 dark:text-white">Vendido</p>
                      <p class="text-xs text-gray-500 dark:text-gray-400">Producto vendido</p>
                    </div>
                    <Icon icon="mdi:shopping" class="ml-auto text-blue-500" v-if="form.estado === 'vendido'" />
                  </div>
                </label>
              </div>
            </div>
            
            <!-- Previsualización -->
            <div v-if="form.nombre" class="mt-4 p-4 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 rounded-xl border border-gray-200 dark:border-gray-700">
              <p class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Vista previa:</p>
              <div class="flex items-start space-x-4">
                <div :class="[
                  'p-3 rounded-lg',
                  form.estado === 'disponible'
                    ? 'bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-900 dark:to-emerald-900'
                    : form.estado === 'vendido'
                    ? 'bg-gradient-to-r from-blue-100 to-indigo-100 dark:from-blue-900 dark:to-indigo-900'
                    : 'bg-gradient-to-r from-amber-100 to-orange-100 dark:from-amber-900 dark:to-orange-900'
                ]">
                  <Icon icon="mdi:package-variant" 
                    :class="form.estado === 'disponible' 
                      ? 'text-green-600 dark:text-green-400'
                      : form.estado === 'vendido'
                      ? 'text-blue-600 dark:text-blue-400'
                      : 'text-amber-600 dark:text-amber-400'" 
                  />
                </div>
                <div class="flex-1">
                  <div class="flex items-start justify-between">
                    <div>
                      <p class="font-medium text-gray-900 dark:text-white">{{ form.nombre }}</p>
                      <p class="text-sm text-gray-500 dark:text-gray-400">
                        {{ form.codigo }}
                      </p>
                      <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        Precio: ${{ formatearMonto(form.precio_estimado_usd) }}
                      </p>
                      <p v-if="form.descripcion" class="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                        {{ form.descripcion }}
                      </p>
                    </div>
                    <span :class="[
                      'px-3 py-1 text-xs font-semibold rounded-full',
                      form.estado === 'disponible'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : form.estado === 'vendido'
                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                        : 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200'
                    ]">
                      {{ convertirEstado(form.estado) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Botones -->
          <div class="mt-8 flex justify-end space-x-3">
            <button
              type="button"
              @click="closeModal"
              class="px-6 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-xl text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-300 hover:shadow-md"
            >
              Cancelar
            </button>
            <button
              type="submit"
              :disabled="saving || !isFormValid"
              :class="[
                'px-6 py-3 rounded-xl font-medium transition-all duration-300 flex items-center',
                isFormValid && !saving
                  ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 hover:shadow-lg'
                  : 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
              ]"
            >
              <span v-if="saving" class="flex items-center">
                <Icon icon="mdi:loading" class="animate-spin mr-2" />
                Guardando...
              </span>
              <span v-else class="flex items-center">
                <Icon :icon="editingProducto ? 'mdi:content-save' : 'mdi:plus-circle'" class="mr-2" />
                {{ editingProducto ? 'Actualizar' : 'Crear' }}
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Modal de detalles -->
    <div v-if="showDetailModal" class="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50">
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md">
        <div class="p-8">
          <div class="flex justify-between items-start mb-6">
            <div>
              <h3 class="text-xl font-bold text-gray-900 dark:text-white mb-2">Detalles del Producto</h3>
              <p class="text-sm text-gray-600 dark:text-gray-400">Información completa del producto</p>
            </div>
            <button @click="closeDetailModal" class="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
              <Icon icon="mdi:close" class="text-gray-500 dark:text-gray-400" />
            </button>
          </div>
          
          <div v-if="selectedProducto" class="space-y-6">
            <div class="flex items-center space-x-4">
              <div :class="[
                'p-4 rounded-xl',
                selectedProducto.estado === 'disponible'
                  ? 'bg-gradient-to-r from-green-500 to-emerald-500'
                  : selectedProducto.estado === 'vendido'
                  ? 'bg-gradient-to-r from-blue-500 to-indigo-500'
                  : 'bg-gradient-to-r from-amber-500 to-orange-500'
              ]">
                <Icon icon="mdi:package-variant" class="text-white text-2xl" />
              </div>
              <div>
                <h4 class="text-lg font-semibold text-gray-900 dark:text-white">{{ selectedProducto.nombre }}</h4>
                <p class="text-sm text-gray-500 dark:text-gray-400">ID: {{ selectedProducto.idProductoRecepcion }}</p>
                <p class="text-xs text-gray-400 font-mono mt-1">Código: {{ selectedProducto.codigo }}</p>
              </div>
            </div>
            
            <div class="space-y-4">
              <div>
                <label class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Descripción</label>
                <p class="mt-1 text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                  {{ selectedProducto.descripcion || 'Sin descripción' }}
                </p>
              </div>
              
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Marca</label>
                  <p class="mt-1 text-gray-900 dark:text-white">{{ selectedProducto.marca || '-' }}</p>
                </div>
                <div>
                  <label class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Modelo</label>
                  <p class="mt-1 text-gray-900 dark:text-white">{{ selectedProducto.modelo || '-' }}</p>
                </div>
              </div>

              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Precio Estimado</label>
                  <p class="mt-1 text-2xl font-bold text-green-600 dark:text-green-400">
                    ${{ formatearMonto(selectedProducto.precio_estimado_usd) }}
                  </p>
                </div>
                <div>
                  <label class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Categoría</label>
                  <p class="mt-1 text-gray-900 dark:text-white">{{ selectedProducto.nombreCategoria || '-' }}</p>
                </div>
              </div>
              
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Estado</label>
                  <span :class="selectedProducto.estado === 'disponible' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : selectedProducto.estado === 'vendido' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' : 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200'" class="inline-block px-3 py-1.5 text-xs font-semibold rounded-full mt-1">
                    {{ convertirEstado(selectedProducto.estado) }}
                  </span>
                </div>
                <div>
                  <label class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Fecha de Creación</label>
                  <div class="mt-1 flex items-center space-x-2 p-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <Icon icon="mdi:calendar" class="text-gray-500" />
                    <div>
                      <p class="text-sm text-gray-900 dark:text-white">{{ formatDate(selectedProducto.fecha_creacion) }}</p>
                      <p class="text-xs text-gray-500 dark:text-gray-400">{{ formatTime(selectedProducto.fecha_creacion) }}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div class="pt-4 border-t border-gray-200 dark:border-gray-700">
                <div class="flex justify-end space-x-3">
                  <button
                    @click="openEditModal(selectedProducto); closeDetailModal()"
                    class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Editar
                  </button>
                  <button
                    @click="closeDetailModal"
                    class="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Cerrar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de Confirmación Personalizado -->
    <div v-if="showConfirmModal" class="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-[60] transition-opacity duration-300">
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md transform transition-all duration-300 scale-100">
        <!-- Encabezado -->
        <div class="px-6 pt-6 pb-4 border-b border-gray-200 dark:border-gray-700">
          <div class="flex items-center space-x-3">
            <div :class="[
              'p-2 rounded-full',
              confirmConfig.type === 'danger' ? 'bg-red-100 dark:bg-red-900/30' : 
              confirmConfig.type === 'warning' ? 'bg-amber-100 dark:bg-amber-900/30' : 
              'bg-blue-100 dark:bg-blue-900/30'
            ]">
              <Icon 
                :icon="confirmConfig.type === 'danger' ? 'mdi:alert-octagon' : 
                       confirmConfig.type === 'warning' ? 'mdi:alert' : 
                       'mdi:information'" 
                :class="[
                  'text-2xl',
                  confirmConfig.type === 'danger' ? 'text-red-600 dark:text-red-400' : 
                  confirmConfig.type === 'warning' ? 'text-amber-600 dark:text-amber-400' : 
                  'text-blue-600 dark:text-blue-400'
                ]"
              />
            </div>
            <h3 class="text-xl font-bold text-gray-900 dark:text-white">{{ confirmConfig.title }}</h3>
          </div>
        </div>

        <!-- Contenido -->
        <div class="px-6 py-4">
          <p class="text-gray-700 dark:text-gray-300" v-html="confirmConfig.message"></p>
          <p v-if="confirmConfig.detail" class="text-sm text-gray-500 dark:text-gray-400 mt-2">{{ confirmConfig.detail }}</p>
        </div>

        <!-- Botones -->
        <div class="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
          <button
            @click="cancelConfirm"
            class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            {{ confirmConfig.cancelText }}
          </button>
          <button
            @click="executeConfirm"
            :class="[
              'px-4 py-2 rounded-lg text-white font-medium transition-colors',
              confirmConfig.type === 'danger' ? 'bg-red-600 hover:bg-red-700' : 
              confirmConfig.type === 'warning' ? 'bg-amber-600 hover:bg-amber-700' : 
              'bg-blue-600 hover:bg-blue-700'
            ]"
          >
            {{ confirmConfig.confirmText }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import { debounce } from 'lodash';
import { useToast } from "vue-toastification";

// Importar funciones de la API
import { 
  obtenerProductosRecepcion,
  crearProductoRecepcion,
  actualizarProductoRecepcion,
  eliminarProductoRecepcion,
  cambiarEstadoProductoRecepcion
} from '@/api/productoRecepcion.js';

import { obtenerCategorias } from '@/api/categorias.js';

export default {
  name: 'ProductosRecepcionList',
  components: {
    Icon,
  },
  data() {
    return {
      productos: [],
      categorias: [],
      loading: false,
      saving: false,
      showModal: false,
      showDetailModal: false,
      showExportMenu: false,
      showConfirmModal: false,
      confirmConfig: {
        title: 'Confirmar',
        message: '',
        detail: '',
        confirmText: 'Aceptar',
        cancelText: 'Cancelar',
        type: 'info',
        onConfirm: null,
        onCancel: null
      },
      searchQuery: '',
      filterStatus: 'all',
      filterCategory: 0,
      precioMin: null,
      precioMax: null,
      sortBy: 'fechaCreacion_desc',
      currentPage: 1,
      itemsPerPage: 10,
      form: {
        idProductoRecepcion: null,
        codigo: '',
        nombre: '',
        descripcion: '',
        marca: '',
        modelo: '',
        precio_estimado_usd: '',
        estado: 'disponible',
        idCategoria: ''
      },
      errors: {},
      editingProducto: null,
      selectedProducto: null,
      lastUpdate: new Date(),
    };
  },
  computed: {
    filteredProductos() {
      let filtered = [...this.productos];
      
      if (this.searchQuery.trim()) {
        filtered = this.buscarProductosLocal(filtered, this.searchQuery);
      }
      
      if (this.filterStatus !== 'all') {
        filtered = filtered.filter(p => p.estado?.toLowerCase() === this.filterStatus);
      }
      
      if (this.filterCategory !== 0) {
        filtered = filtered.filter(p => p.idCategoria === this.filterCategory);
      }
      
      if (this.precioMin !== null && this.precioMin !== '') {
        filtered = filtered.filter(p => (p.precio_estimado_usd || 0) >= this.precioMin);
      }
      
      if (this.precioMax !== null && this.precioMax !== '') {
        filtered = filtered.filter(p => (p.precio_estimado_usd || 0) <= this.precioMax);
      }
      
      filtered = this.ordenarProductos(filtered, this.sortBy);
      
      return filtered;
    },
    
    estadisticas() {
      const disponibles = this.productos.filter(p => p.estado?.toLowerCase() === 'disponible').length;
      const vendidos = this.productos.filter(p => p.estado?.toLowerCase() === 'vendido').length;
      const reservados = this.productos.filter(p => p.estado?.toLowerCase() === 'reservado').length;
      const valorTotal = this.productos.reduce((sum, p) => sum + (p.precio_estimado_usd || 0), 0);
      const recientes = this.productos.filter(p => {
        const fecha = new Date(p.fecha_creacion);
        const hace7Dias = new Date();
        hace7Dias.setDate(hace7Dias.getDate() - 7);
        return fecha >= hace7Dias;
      }).length;
      
      const total = this.productos.length;
      const porcentajeDisponibles = total > 0 ? ((disponibles / total) * 100).toFixed(1) : 0;
      
      return {
        disponibles,
        vendidos,
        reservados,
        valorTotal,
        recientes,
        porcentajeDisponibles
      };
    },
    
    paginatedProductos() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredProductos.slice(start, end);
    },
    
    totalPages() {
      return Math.ceil(this.filteredProductos.length / this.itemsPerPage);
    },
    
    visiblePages() {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);
      
      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1);
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    },
    
    startItem() {
      return (this.currentPage - 1) * this.itemsPerPage + 1;
    },
    
    endItem() {
      return Math.min(this.currentPage * this.itemsPerPage, this.filteredProductos.length);
    },
    
    isFormValid() {
      return this.form.codigo.trim() && 
             this.form.nombre.trim() && 
             this.form.idCategoria && 
             this.form.precio_estimado_usd > 0;
    },
    
    isFiltered() {
      return this.searchQuery.trim() || 
             this.filterStatus !== 'all' || 
             this.filterCategory !== 0 ||
             (this.precioMin !== null && this.precioMin !== '') ||
             (this.precioMax !== null && this.precioMax !== '');
    },
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  mounted() {
    this.loadProductos();
    this.loadCategorias();
  },
  watch: {
    filterStatus() {
      this.currentPage = 1;
    },
    filterCategory() {
      this.currentPage = 1;
    },
    searchQuery() {
      this.currentPage = 1;
    },
    sortBy() {
      this.currentPage = 1;
    },
  },
  methods: {
    formatearMonto(numero, decimales = 2) {
      if (numero === null || numero === undefined || numero === '' || isNaN(numero)) {
        return decimales === 2 ? '0.00' : '0';
      }
      const num = Number(numero);
      if (isNaN(num)) return decimales === 2 ? '0.00' : '0';
      return num.toLocaleString('es-VE', { minimumFractionDigits: decimales, maximumFractionDigits: decimales });
    },
    
    buscarProductosLocal(productos, termino) {
      if (!termino?.trim()) return productos;
      const terminoLower = termino.toLowerCase().trim();
      return productos.filter(producto => 
        (producto.codigo?.toLowerCase().includes(terminoLower)) ||
        (producto.nombre?.toLowerCase().includes(terminoLower)) ||
        (producto.descripcion?.toLowerCase().includes(terminoLower)) ||
        (producto.marca?.toLowerCase().includes(terminoLower)) ||
        (producto.modelo?.toLowerCase().includes(terminoLower)) ||
        (producto.nombreCategoria?.toLowerCase().includes(terminoLower))
      );
    },
    
    ordenarProductos(productos, criterio) {
      const array = [...productos];
      const criterios = {
        nombre: (a, b) => (a.nombre || '').localeCompare(b.nombre || ''),
        nombre_desc: (a, b) => (b.nombre || '').localeCompare(a.nombre || ''),
        codigo: (a, b) => (a.codigo || '').localeCompare(b.codigo || ''),
        codigo_desc: (a, b) => (b.codigo || '').localeCompare(a.codigo || ''),
        precio_desc: (a, b) => Number(b.precio_estimado_usd || 0) - Number(a.precio_estimado_usd || 0),
        precio: (a, b) => Number(a.precio_estimado_usd || 0) - Number(b.precio_estimado_usd || 0),
        fechaCreacion_desc: (a, b) => new Date(b.fecha_creacion || 0) - new Date(a.fecha_creacion || 0),
        fechaCreacion: (a, b) => new Date(a.fecha_creacion || 0) - new Date(b.fecha_creacion || 0)
      };
      return array.sort(criterios[criterio] || criterios.fechaCreacion_desc);
    },
    
    convertirEstado(estado) {
      if (!estado) return 'Sin estado';
      const estadoLower = estado.toLowerCase();
      if (estadoLower === 'disponible') return 'Disponible';
      if (estadoLower === 'vendido') return 'Vendido';
      if (estadoLower === 'reservado') return 'Reservado';
      return estado;
    },
    
    getCategoriaNombre(id) {
      const cat = this.categorias.find(c => c.idCategoria === id);
      return cat ? cat.nombre : '';
    },
    
    showConfirm(options) {
      return new Promise((resolve) => {
        this.confirmConfig = {
          title: options.title || 'Confirmar',
          message: options.message || '¿Estás seguro?',
          detail: options.detail || '',
          confirmText: options.confirmText || 'Aceptar',
          cancelText: options.cancelText || 'Cancelar',
          type: options.type || 'info',
          onConfirm: () => resolve(true),
          onCancel: () => resolve(false)
        };
        this.showConfirmModal = true;
      });
    },
    
    cancelConfirm() {
      this.showConfirmModal = false;
      if (this.confirmConfig.onCancel) {
        this.confirmConfig.onCancel();
      }
    },
    
    executeConfirm() {
      this.showConfirmModal = false;
      if (this.confirmConfig.onConfirm) {
        this.confirmConfig.onConfirm();
      }
    },
    
    async loadProductos() {
      this.loading = true;
      try {
        const response = await obtenerProductosRecepcion('todos');
        if (response.success) {
          this.productos = response.data;
          this.lastUpdate = new Date();
        } else {
          throw new Error(response.error || 'Error al cargar productos');
        }
      } catch (error) {
        console.error('❌ Error al cargar productos:', error);
        this.toast.error('Error al cargar los productos: ' + error.message);
      } finally {
        this.loading = false;
      }
    },
    
    async loadCategorias() {
      try {
        const response = await obtenerCategorias('all');
        if (response.success) {
          this.categorias = response.data;
        }
      } catch (error) {
        console.error('❌ Error al cargar categorías:', error);
      }
    },
    
    handleFilterChange() {
      this.currentPage = 1;
    },
    
    handleSearch: debounce(function() {
      this.currentPage = 1;
    }, 300),
    
    openCreateModal() {
      this.editingProducto = null;
      this.resetForm();
      this.errors = {};
      this.showModal = true;
    },
    
    openEditModal(producto) {
      this.editingProducto = producto;
      this.form = {
        idProductoRecepcion: producto.idProductoRecepcion,
        codigo: producto.codigo,
        nombre: producto.nombre,
        descripcion: producto.descripcion || '',
        marca: producto.marca || '',
        modelo: producto.modelo || '',
        precio_estimado_usd: producto.precio_estimado_usd,
        estado: producto.estado,
        idCategoria: producto.idCategoria
      };
      this.errors = {};
      this.showModal = true;
    },
    
    openDetailModal(producto) {
      this.selectedProducto = producto;
      this.showDetailModal = true;
    },
    
    closeModal() {
      this.showModal = false;
      this.resetForm();
      this.errors = {};
    },
    
    closeDetailModal() {
      this.showDetailModal = false;
      this.selectedProducto = null;
    },
    
    resetForm() {
      this.form = {
        idProductoRecepcion: null,
        codigo: '',
        nombre: '',
        descripcion: '',
        marca: '',
        modelo: '',
        precio_estimado_usd: '',
        estado: 'disponible',
        idCategoria: ''
      };
      this.editingProducto = null;
    },
    
    validateForm() {
      this.errors = {};
      
      if (!this.form.codigo.trim()) {
        this.errors.codigo = 'El código es requerido';
      } else if (this.form.codigo.length > 50) {
        this.errors.codigo = 'El código no puede exceder 50 caracteres';
      }
      
      if (!this.form.nombre.trim()) {
        this.errors.nombre = 'El nombre es requerido';
      } else if (this.form.nombre.length > 200) {
        this.errors.nombre = 'El nombre no puede exceder 200 caracteres';
      }
      
      if (this.form.descripcion && this.form.descripcion.length > 500) {
        this.errors.descripcion = 'La descripción no puede exceder 500 caracteres';
      }
      
      if (!this.form.idCategoria) {
        this.errors.idCategoria = 'Debe seleccionar una categoría';
      }
      
      if (!this.form.precio_estimado_usd || this.form.precio_estimado_usd <= 0) {
        this.errors.precio = 'El precio debe ser mayor a 0';
      }
      
      return Object.keys(this.errors).length === 0;
    },
    
    async saveProducto() {
      if (!this.validateForm()) {
        return;
      }
      
      this.saving = true;
      try {
        let result;
        if (this.editingProducto) {
          result = await actualizarProductoRecepcion(this.editingProducto.idProductoRecepcion, this.form);
        } else {
          result = await crearProductoRecepcion(this.form);
        }
        
        if (result.success) {
          await this.loadProductos();
          this.closeModal();
          this.toast.success(
            this.editingProducto ? 'Producto actualizado correctamente' : 'Producto creado correctamente'
          );
        } else {
          throw new Error(result.error || 'Error al guardar');
        }
      } catch (error) {
        console.error('❌ Error al guardar producto:', error);
        this.toast.error(error.message || 'Error al guardar el producto');
      } finally {
        this.saving = false;
      }
    },
    
    async marcarComoVendido(producto) {
      const confirmed = await this.showConfirm({
        title: 'Marcar como vendido',
        message: `¿Marcar <strong>"${producto.nombre}"</strong> como vendido?`,
        detail: 'Esta acción cambiará el estado del producto.',
        confirmText: 'Sí, marcar como vendido',
        cancelText: 'Cancelar',
        type: 'warning'
      });
      
      if (confirmed) {
        try {
          const result = await cambiarEstadoProductoRecepcion(producto.idProductoRecepcion, 'vendido');
          if (result.success) {
            await this.loadProductos();
            this.toast.success('Producto marcado como vendido');
          } else {
            throw new Error(result.error || 'Error al cambiar estado');
          }
        } catch (error) {
          console.error('❌ Error al marcar como vendido:', error);
          this.toast.error(error.message || 'Error al marcar el producto como vendido');
        }
      }
    },
    
    async toggleStatus(producto) {
      if (producto.estado === 'disponible') {
        await this.marcarComoVendido(producto);
      }
    },
    
    async confirmDelete(producto) {
      const confirmed = await this.showConfirm({
        title: '¿Eliminar producto?',
        message: `¿Estás seguro de eliminar <strong>"${producto.nombre}"</strong>?`,
        detail: 'Esta acción no se puede deshacer.',
        confirmText: 'Sí, eliminar',
        cancelText: 'Cancelar',
        type: 'danger'
      });
      
      if (confirmed) {
        await this.deleteProducto(producto.idProductoRecepcion, producto.nombre);
      }
    },
    
    async deleteProducto(id, nombre) {
      try {
        const result = await eliminarProductoRecepcion(id);
        if (result.success) {
          await this.loadProductos();
          this.toast.success(`El producto "${nombre}" ha sido eliminado`);
        } else {
          throw new Error(result.error || 'Error al eliminar');
        }
      } catch (error) {
        console.error('❌ Error al eliminar producto:', error);
        this.toast.error(error.message || 'Error al eliminar el producto');
      }
    },
    
    async printFullReport() {
      if (this.filteredProductos.length === 0) {
        this.toast.warning('No hay datos para imprimir');
        return;
      }
      
      try {
        this.toast.info('Funcionalidad de reporte en desarrollo');
        this.showExportMenu = false;
      } catch (error) {
        console.error('❌ Error generando reporte:', error);
        this.toast.error('Error al generar el reporte: ' + error.message);
      }
    },
    
    printSimplifiedReport() {
      this.printFullReport();
    },
    
    printCurrentView() {
      this.printFullReport();
    },
    
    clearFilters() {
      this.searchQuery = '';
      this.filterStatus = 'all';
      this.filterCategory = 0;
      this.precioMin = null;
      this.precioMax = null;
      this.sortBy = 'fechaCreacion_desc';
      this.currentPage = 1;
      this.loadProductos();
    },
    
    formatDate(fechaISO) {
      if (!fechaISO) return '';
      try {
        const fecha = new Date(fechaISO);
        return fecha.toLocaleDateString('es-ES', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      } catch (error) {
        return fechaISO;
      }
    },
    
    formatTime(fechaISO) {
      if (!fechaISO) return '';
      try {
        const fecha = new Date(fechaISO);
        return fecha.toLocaleTimeString('es-ES', {
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (error) {
        return '';
      }
    },
  },
};
</script>

<style scoped>
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}

.fade-in {
  animation: fadeIn 0.3s ease-out;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.dark ::-webkit-scrollbar-track {
  background: #374151;
}

.dark ::-webkit-scrollbar-thumb {
  background: #6b7280;
}

.dark ::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}
</style>